/**
 * E3-FO-E — session cache of GET /mod/elc/setup.
 *
 * The setup gate wraps every ELC route, so the status is fetched once per
 * session (per signed-in user) and shared; `refreshElcSetup()` refetches
 * after the setup page saves. A failed fetch is surfaced as `error` and the
 * gate fails open (the backend still enforces).
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { getElcSetup, type SetupStatus } from '../api/elcSetupApi';

export type ElcSetupState =
  | { status: 'idle' | 'loading'; setup: null }
  | { status: 'ready'; setup: SetupStatus }
  | { status: 'error'; setup: null };

let state: ElcSetupState = { status: 'idle', setup: null };
let owner: string | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: ElcSetupState) {
  state = next;
  listeners.forEach((l) => l());
}

/** Fetch (or refetch) the setup status; resolves when the store is updated. */
export function refreshElcSetup(): Promise<void> {
  if (inflight) return inflight;
  // Keep showing the last known status while refetching; only the first load blanks.
  if (state.status !== 'ready') publish({ status: 'loading', setup: null });
  inflight = getElcSetup()
    .then((setup) => publish({ status: 'ready', setup }))
    .catch(() => publish({ status: 'error', setup: null }))
    .finally(() => { inflight = null; });
  return inflight;
}

/** Forget the cached status (sign-out / user switch / tests). */
export function resetElcSetup() {
  owner = null;
  inflight = null;
  publish({ status: 'idle', setup: null });
}

export function useElcSetup(): ElcSetupState & { refresh: () => Promise<void> } {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const snapshot = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state,
  );
  useEffect(() => {
    if (!userId) return;
    if (owner !== userId) {
      owner = userId;
      inflight = null;
      publish({ status: 'idle', setup: null });
    }
    if (state.status === 'idle') void refreshElcSetup();
  }, [userId]);
  // A different user's cached status is never shown (before the effect resets it).
  const current: ElcSetupState = owner === userId ? snapshot : { status: 'idle', setup: null };
  return { ...current, refresh: refreshElcSetup };
}
